import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
  Input,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { minioService } from '../../api/services/minioService';

const VisuallyHiddenInput = styled(Input)({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const Dropzone = styled(Paper)(({ theme, isDragging, size }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: size === 'circular' ? '50%' : theme.spacing(1.5),
  padding: size === 'thumbnail' ? theme.spacing(1) : theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: isDragging ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
  minHeight: size === 'thumbnail' ? 100 : size === 'circular' ? 150 : 150,
  maxWidth: size === 'thumbnail' ? 130 : size === 'circular' ? 180 : '100%',
  width: size === 'thumbnail' ? 130 : size === 'circular' ? 150 : '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const PreviewContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const PreviewImage = styled('img')(({ theme, circular, thumbnail }) => ({
  maxHeight: 150,
  maxWidth: '100%',
  borderRadius: circular ? '50%' : theme.spacing(0.5),
  width: circular ? 120 : thumbnail ? 100 : 'auto',
  height: circular ? 120 : thumbnail ? 100 : 'auto',
  objectFit: 'cover',
  border: circular ? `3px solid ${theme.palette.common.white}` : 'none',
  boxShadow: circular ? theme.shadows[2] : 'none',
}));

const Overlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.3rem',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  borderRadius: 'inherit',
  '&:hover': {
    opacity: 1,
  },
});

const UploadingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  padding: 1,
});

const PlaceholderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
});

const ImageUpload = ({
  name,
  label,
  onChange,
  value,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024,
  folder = 'uploads',
  required = false,
  disabled = false,
  preview = true,
  className = '',
  thumbnail = false,
  circular = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploadedData, setUploadedData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (value) {
      setPreviewUrl(value);
      if (typeof value === 'string' && value.startsWith('http')) {
        setUploadedData({
          url: value,
          filename: value.split('/').pop() || 'Image',
        });
        setShowSuccessAlert(false);
      }
    } else {
      setPreviewUrl('');
      setUploadedData(null);
      setShowSuccessAlert(false);
    }
  }, [value]);

  const uploadFile = async (fileObj) => {
    setUploading(true);
    setError('');
    setUploadProgress(0);
    setShowSuccessAlert(false);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await minioService.uploadFile(fileObj, folder);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        const fileData = response.data;
        setUploadedData(fileData);
        setPreviewUrl(fileData.url);
        setShowSuccessAlert(true);
        
        enqueueSnackbar(`${fileData.filename} uploaded successfully!`, { 
          variant: 'success',
          autoHideDuration: 3000,
        });
        
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: fileData.url,
              fileData: fileData,
            }
          });
        }
        
        return fileData;
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'File upload failed';
      setError(errorMsg);
      
      enqueueSnackbar(errorMsg, { 
        variant: 'error',
        autoHideDuration: 5000,
      });
      
      throw err;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileObj = selectedFiles[0];

    if (fileObj.size > maxSize) {
      const errorMsg = `File size exceeds ${maxSize / (1024 * 1024)}MB limit`;
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
      return;
    }

    if (accept !== '*' && accept !== '') {
      const acceptedTypes = accept.split(',').map(type => type.trim());
      const fileType = fileObj.type;
      
      const isValidType = acceptedTypes.some(type => {
        if (type.includes('/*')) {
          const category = type.split('/')[0];
          return fileType.startsWith(category);
        }
        return fileType === type;
      });

      if (!isValidType) {
        const errorMsg = `File type not supported. Accepted: ${accept}`;
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
        return;
      }
    }

    setError('');

    if (preview) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(fileObj);
    }

    try {
      await uploadFile(fileObj);
    } catch (err) {
      if (preview && value) {
        setPreviewUrl(value);
      } else {
        setPreviewUrl('');
      }
    }
  };

  const handleRemoveFile = () => {
    setPreviewUrl(value || '');
    setUploadedData(null);
    setError('');
    setShowSuccessAlert(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: '',
          fileData: null,
        }
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const fileObj = droppedFiles[0];
      const changeEvent = {
        target: {
          files: [fileObj],
        }
      };
      handleFileChange(changeEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleCopyUrl = async () => {
    if (uploadedData?.url) {
      try {
        await navigator.clipboard.writeText(uploadedData.url);
        enqueueSnackbar('URL copied to clipboard!', { variant: 'info' });
      } catch (err) {
        console.error('Failed to copy URL:', err);
        enqueueSnackbar('Failed to copy URL', { variant: 'error' });
      }
    }
  };

  const handleViewFile = () => {
    if (uploadedData?.url) {
      window.open(uploadedData.url, '_blank');
    }
  };

  const getSize = () => {
    if (thumbnail) return 'thumbnail';
    if (circular) return 'circular';
    return 'default';
  };

  return (
    <Box className={className} sx={{ mb: 1 }}>
      <Typography
        component="label"
        variant="caption"
        sx={{
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
          color: 'text.secondary',
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
        {required && <span style={{ color: 'error.main', marginLeft: 4 }}>*</span>}
      </Typography>

      <VisuallyHiddenInput
        type="file"
        inputRef={fileInputRef}
        name={name}
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || uploading}
      />

      <Dropzone
        elevation={0}
        isDragging={isDragging}
        size={getSize()}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <UploadingContainer>
            <CircularProgress size={30} thickness={4} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {uploadProgress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ 
                width: '70%', 
                height: 4, 
                borderRadius: 2,
                mt: 0.5,
              }}
            />
          </UploadingContainer>
        ) : preview && previewUrl ? (
          <PreviewContainer>
            <PreviewImage
              src={previewUrl}
              alt="Upload preview"
              circular={circular}
              thumbnail={thumbnail}
            />
            {!disabled && (
              <Overlay>
                <Tooltip title="View">
                  <IconButton
                    size="small"
                    sx={{ 
                      bgcolor: 'common.white',
                      '&:hover': { bgcolor: 'grey.100' },
                      width: 28,
                      height: 28,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewFile();
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy URL">
                  <IconButton
                    size="small"
                    sx={{ 
                      bgcolor: 'common.white',
                      '&:hover': { bgcolor: 'grey.100' },
                      width: 28,
                      height: 28,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyUrl();
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    sx={{ 
                      bgcolor: 'common.white',
                      '&:hover': { bgcolor: 'error.main', color: 'common.white' },
                      width: 28,
                      height: 28,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Overlay>
            )}
          </PreviewContainer>
        ) : (
          <PlaceholderContainer>
            <CloudUploadIcon 
              sx={{ 
                fontSize: 32, 
                color: 'text.secondary',
                opacity: 0.7,
              }} 
            />
            <Typography variant="caption" color="text.primary" sx={{ mt: 0.5 }}>
              {disabled ? 'No file' : 'Upload image'}
            </Typography>
          </PlaceholderContainer>
        )}
      </Dropzone>

      {showSuccessAlert && uploadedData && !uploading && (
        <Alert
          severity="success"
          sx={{ mt: 1 }}
          icon={<ImageIcon sx={{ fontSize: 16 }} />}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowSuccessAlert(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <Typography variant="caption">
            Uploaded successfully
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ImageUpload;